<template>
  <TaskPage ref="taskPageRef" no-brand no-auto-back :no-back="isUploadingRef" :can-continue="!isUploadingRef && !!selectedFileRef && isFileSupportedRef" @action="onUpload" @back="emit('back')">
    <template #title>Upload my tree</template>

    <div v-if="sourceErrorRef" class="alert alert-primary alert-sm source-error">
      {{ sourceErrorRef }}
    </div>

    <div v-if="selectedFileRef" class="file-area">
      <SyncSourceHeader v-if="sourceRef" :source="sourceRef"/>
      <button type="button" v-if="!isUploadingRef" class="btn btn-inline btn-link change-btn" @click="onChangeFile">
        Change
      </button>
      <div v-if="!isFileSupportedRef" class="alert alert-primary alert-sm not-supported">
        <template v-if="selectedTypeInfoRef">
          We're working on support for {{ selectedTypeInfoRef.product }}, but it's not ready yet.
        </template>
        <template v-else>
          We don't recognize this type of family tree file.
        </template>
      </div>
    </div>

    <div v-else class="no-file">
      <label class="drop-zone" :class="{ 'drag-over': isDragOverRef }"
        @dragenter.prevent="isDragOverRef = true"
        @dragover.prevent="isDragOverRef = true"
        @dragleave="isDragOverRef = false"
        @drop.prevent="onFileDropped"
      >
        <input type="file" :accept="acceptAttrRef" class="visually-hidden" @change="onFileSelected"/>
        <div class="drop-instruction">Drag here or choose a file</div>
        <span class="btn btn-primary choose-btn">Choose a file</span>
      </label>

      <div class="supported-types">
        <button type="button" class="btn btn-link" data-bs-toggle="collapse" data-bs-target="#supportGrid"
          aria-expanded="false" aria-controls="supportGrid">
          What genealogy file types are supported?
        </button>

        <div id="supportGrid" class="collapse">
          <AppSupportGrid compact/>
        </div>
      </div>
    </div>

    <div v-if="isUploadingRef" class="upload-progress">
      <ProgressBar :value="uploadPercentRef" :max="100" smooth/>
      <div class="upload-status">Uploading your tree...</div>
    </div>

    <template v-if="isUploadingRef" #secondaryAction>
      <button type="button" class="btn btn-outline-primary" @click="onCancelUpload">
        Cancel
      </button>
    </template>
    <template #actionText>Upload</template>

    <Modal ref="abandonModalRef" ok-style="danger" @ok="abandonConfirmation.confirm" @hidden="abandonConfirmation.dismiss">
      <template #title>Cancel upload?</template>
      Your file is still uploading. If you cancel now, the upload will be discarded.
      <template #okText>Cancel upload</template>
      <template #cancelText>Continue uploading</template>
    </Modal>

    <WorkspaceMatchModal ref="matchModalRef" :matches="matchesRef"
      @update="onUpdateExisting" @create-new="onCreateNew" @cancel="onCancelMatch"/>
  </TaskPage>
</template>

<style lang="scss" scoped>
.file-area {
  .alert {
    margin-top: 1rem;
  }
}

.source-error {
  margin-bottom: 1rem;
}

.no-file {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.drop-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
  border: 2px dashed #ccc;
  cursor: pointer;

  &:hover,
  &.drag-over {
    border-color: #aaa;
    background-color: #f8f8f8;
  }

  &.drag-over > * {
    pointer-events: none;
  }

  .drop-instruction {
    color: #aaa;
  }

  .choose-btn {
    min-width: 160px;
  }
}

.supported-types {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .btn-link {
    align-self: flex-start;
    padding: 0;
    font-size: 0.875rem;
  }
}

.change-btn {
  font-size: 0.875rem;
}

.upload-progress {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  .upload-status {
    font-size: 0.875rem;
    color: #888;
  }
}

</style>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { onBeforeRouteLeave, useRouter } from 'vue-router'
import { usePageTitle, unexpectedErrorEvent } from '@webapp/util/AppUtil'
import { useTelemetry } from '@webapp/util/Telemetry'
import { useConfirmation } from '@webapp/util/Confirmation'
import { useSyncSessionStore } from './SyncSessionStore'
import { useWorkspaceFinder, WorkspaceMatch } from './WorkspaceFinder'
import { SourceState, toSyncSource } from './SyncSessionModel'
import { currentOS, getSupportedFileExtensions, workspaceTypeInfos } from '@/rd/ResearchDataModel'
import { useTokenManager } from '@webapp/auth/TokenManager'
import { isTestStringId } from '@webapp/util/TestId'
import TaskPage from '@webapp/util/TaskPage.vue'
import ProgressBar from '@webapp/util/ProgressBar.vue'
import Modal from '@webapp/util/Modal.vue'
import SyncSourceHeader from './SyncSourceHeader.vue'
import WorkspaceMatchModal from './WorkspaceMatchModal.vue'
import AppSupportGrid from '@/public/AppSupportGrid.vue'
import { AsyncUtil } from '@webapp/util/AsyncUtil'

const emit = defineEmits(['uploaded', 'back'])
const router = useRouter()
const syncSessionStore = useSyncSessionStore()
const tokenManager = useTokenManager()
const workspaceFinder = useWorkspaceFinder()
const { trackException } = useTelemetry()

const isTestUserRef = computed(() => isTestStringId(tokenManager.current.userId))

const isUploadingRef = ref(false)
const uploadPercentRef = ref(0)
const isDragOverRef = ref(false)
const sourceErrorRef = ref<string>()
const selectedFileRef = ref<File>()
const sessionIdRef = ref<string>()
const sourceRef = computed(() => selectedFileRef.value ? toSyncSource(selectedFileRef.value) : undefined)
const taskPageRef = ref<InstanceType<typeof TaskPage>>()
const abandonModalRef = ref<InstanceType<typeof Modal>>()
const matchModalRef = ref<InstanceType<typeof WorkspaceMatchModal>>()
const matchesRef = ref<WorkspaceMatch[]>([])
const pendingFileRef = ref<File>()
const abandonConfirmation = useConfirmation(abandonModalRef)

const selectedTypeInfoRef = computed(() =>
  selectedFileRef.value ? typeInfoForName(selectedFileRef.value.name) : undefined)

function typeInfoForName(name: string) {
  const m = name.match(/\.[^.]+$/)
  const ext = m ? m[0].toLowerCase() : ''
  const typeInfo = ext ? workspaceTypeInfos.find(t => t.fileExtensions.includes(ext)) : undefined
  return typeInfo && (!typeInfo.hidden || isTestUserRef.value) ? typeInfo : undefined
}

const isFileSupportedRef = computed(() =>
  !!currentOS && !!selectedTypeInfoRef.value?.supportedOn.includes(currentOS),
)

const acceptAttrRef = computed(() => getSupportedFileExtensions(currentOS, isTestUserRef.value).join(','))

let abortController: AbortController | undefined

usePageTitle('Upload my tree')

function onFileSelected(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = '' // let the same file be re-picked after a cancel
  if (file) {
    selectFile(file)
  }
}

function onFileDropped(event: DragEvent) {
  isDragOverRef.value = false
  const file = event.dataTransfer?.files?.[0]
  if (file) {
    selectFile(file)
  }
}

// A same-named existing tree means this is really a re-sync, which is done on that tree's manage
// page -- offer that before committing the file. Best-effort: a failure just selects the file.
async function selectFile(file: File) {
  sourceErrorRef.value = undefined
  try {
    const matches = await workspaceFinder.findWorkspacesWithFileNameAsync(file.name)
    if (matches.length) {
      matchesRef.value = matches
      pendingFileRef.value = file
      matchModalRef.value?.open()
      return
    }
  }
  catch (error) {
    trackException(error)
  }
  commitFile(file)
}

// Committing a supported file starts its upload right away -- there's no separate confirm step.
// An unsupported file just stays selected so the "not supported" notice can prompt a change.
function commitFile(file: File) {
  selectedFileRef.value = file
  if (isFileSupportedRef.value) {
    onUpload()
  }
}

function onChangeFile() {
  selectedFileRef.value = undefined
}

async function onUpload() {
  if (!selectedFileRef.value) {
    return
  }
  isUploadingRef.value = true
  uploadPercentRef.value = 0
  abortController = new AbortController()

  try {
    const sessionId = sessionIdRef.value ?? (sessionIdRef.value = await syncSessionStore.createSessionAsync())

    await AsyncUtil.atLeast(500, syncSessionStore.uploadFileAsync(
      sessionId,
      selectedFileRef.value,
      (loaded, total) => {
        uploadPercentRef.value = total > 0 ? Math.round((loaded / total) * 100) : 0
      },
      abortController.signal,
    ))

    // The server opens the file during refresh; only a Ready source can move on. A re-upload
    // to the same session is rejected, so a bad file discards the session and starts over.
    await syncSessionStore.refreshSessionAsync(sessionId)
    isUploadingRef.value = false
    if (syncSessionStore.getSession(sessionId)?.sourceState !== SourceState.Ready) {
      sourceErrorRef.value = sourceErrorMessage()
      await syncSessionStore.abandonSessionAsync(sessionId)
      sessionIdRef.value = undefined
      return
    }
    emit('uploaded', sessionId)
  }
  catch (error) {
    if ((error as Error).name === 'AbortError') {
      return
    }
    isUploadingRef.value = false
    unexpectedErrorEvent.trigger({ error })
  }
  finally {
    abortController = undefined
    taskPageRef.value?.setBusy(false)
  }
}

function onUpdateExisting(workspaceId: string) {
  // Re-sync happens on the tree's manage page, not here -- hand off there without uploading.
  const dataGroupId = matchesRef.value.find(m => m.workspaceId === workspaceId)?.dataGroupId
  if (dataGroupId) {
    router.push({ name: 'researchGroup.manageTab', params: { dataGroupId } })
  }
}

function onCreateNew() {
  // Commit the file as a new tree -- this starts the upload, same as a first-time selection.
  if (pendingFileRef.value) {
    const file = pendingFileRef.value
    pendingFileRef.value = undefined
    commitFile(file)
  }
}

function onCancelMatch() {
  // Didn't choose -- discard the pending file so they can pick a different one.
  pendingFileRef.value = undefined
}

function sourceErrorMessage() {
  const session = syncSessionStore.getSession(sessionIdRef.value)
  if (session?.sourceState === SourceState.Unrecognized) {
    return "We don't recognize this type of family tree file."
  }
  if (session?.isSourceOutdated) {
    const product = selectedTypeInfoRef.value?.product ?? 'family tree'
    return `This looks like an older ${product} file we can't read yet. Save it again in a newer version, then upload that.`
  }
  return "We couldn't read this file. It may be damaged or an unsupported version."
}

async function onCancelUpload() {
  const confirmed = await abandonConfirmation.ask()
  if (confirmed) {
    await abandonUploadAsync()
  }
}

async function abandonUploadAsync() {
  abortController?.abort()
  isUploadingRef.value = false
  await syncSessionStore.abandonSessionAsync(sessionIdRef.value)
  sessionIdRef.value = undefined
}

onMounted(() => {
  window.addEventListener('beforeunload', handleBeforeUnload)
})

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
})

function handleBeforeUnload(e: BeforeUnloadEvent) {
  if (isUploadingRef.value) {
    e.preventDefault()
    e.returnValue = ''
  }
}

onBeforeRouteLeave(async () => {
  if (!isUploadingRef.value) {
    return true
  }
  const confirmed = await abandonConfirmation.ask()
  if (confirmed) {
    await abandonUploadAsync()
  }
  return confirmed
})

</script>
